import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Sun, Moon, X, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react'
import { useTheme } from '@helpers/ThemeContext'
import { useScrolled } from '@helpers/useScrolled'
import { useWindowSize } from '@helpers/useWindowSize'
import { NAV_LINKS, SITE, ROUTES } from '@/constants'
import Button from '@ui/Button'
import ReactDOM from 'react-dom'
const resolveIconSrc = (icon) => (icon?.startsWith('/') ? icon : `/${icon}`)
// ── One item inside a mega-menu panel: icon tile + title + description ──
function MegaItem({ item, onClose }) {
  return (
    <Link to={item.href} className="mega-item" onClick={onClose}>
       <span
        className="mega-item-icon"
        style={{ '--icon-url': `url(${resolveIconSrc(item.icon)})` }}
      >
        <span className="mega-item-icon-mask" aria-hidden="true" />
      </span>

      <span className="mega-item-body">
        <span className={`mega-item-title ${item.highlight ? 'mega-item-title--highlight' : ''}`}>
          {item.label}
        </span>
        <span className="mega-item-desc">
          {item.pageDesc?.length > 250 ? `${item.pageDesc.slice(0, 250)}...` : item.pageDesc}
        </span>
      </span>
    </Link>
  )
}

// ── Desktop nav item: plain link, or a mega-menu trigger. ──
// The panel itself no longer lives here — it's a single shared panel
// rendered once at the Navbar level (see <Navbar> below) so it can render
// full-width, anchored to the navbar bar instead of each individual item.
// This component only owns the trigger pill and reports hover/focus/click
// up to the parent via onOpen/onClose.
function NavItem({ link, isOpen, onOpen, onClose, setTriggerRef, onInstantClose }) {
  const hasChildren = link.children?.length > 0

  if (!hasChildren) {
    return (
      <Link to={link.href} className="nav-item-trigger" onMouseEnter={onInstantClose}>
        {link.label}
      </Link>
    )
  }

  const toggle = () => (isOpen ? onClose(true) : onOpen(link))

  return (
    <div
      className="nav-item-dropdown"
      onMouseEnter={() => onOpen(link)}
      onMouseLeave={() => onClose()}
      onFocus={() => onOpen(link)}
    >
      <div className={`nav-item-trigger ${isOpen ? 'nav-item-trigger--open' : ''}`}>
        {link.href === '' ? (
          /* No real page — a single button opens/closes the panel */
          <button
            ref={setTriggerRef}
            type="button"
            className="nav-item-trigger-btn"
            aria-haspopup="true"
            aria-expanded={isOpen}
            onClick={toggle}
          >
            <span>{link.label}</span>
            <ChevronDown size={15} className={`nav-chevron ${isOpen ? 'nav-chevron--open' : ''}`} />
          </button>
        ) : (
          /* Has a real page — label navigates, chevron button toggles the panel */
          <>
            <Link ref={setTriggerRef} to={link.href} className="nav-item-trigger-label">
              {link.label}
            </Link>
            <button
              type="button"
              className="nav-item-trigger-toggle"
              aria-haspopup="true"
              aria-expanded={isOpen}
              aria-label={`${link.label} submenu`}
              onClick={toggle}
            >
              <ChevronDown size={15} className={`nav-chevron ${isOpen ? 'nav-chevron--open' : ''}`} />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ── Mobile root-level item: plain link, or a drill-in trigger ──
// NOTE: submenus are no longer an inline accordion — tapping a parent
// now slides the whole panel to a dedicated submenu view (see
// MobileSubmenu + the track markup inside the main Navbar render).
function MobileNavItem({ link, isActive, onNavigate, onDrillIn }) {
  const hasChildren = link.children?.length > 0

  if (!hasChildren) {
    return (
      <Link
        to={link.href}
        onClick={onNavigate}
                className={`mobile-nav-trigger ${isActive ? 'mobile-nav-trigger--active' : ''}`}
      >
        {link.label}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={onDrillIn}
      className={`mobile-nav-trigger ${isActive ? 'mobile-nav-trigger--active' : ''}`}
    >
      {link.label}
      <ChevronRight size={16} className="nav-chevron-right" aria-hidden="true" />
    </button>
  )
}

function MobileSubmenu({ link, onBack, onNavigate, onDrillGroup }) {
  const backRef = useRef(null)
  useEffect(() => {
    const t = setTimeout(() => backRef.current?.focus(), 360)
    return () => clearTimeout(t)
  }, [link])

  return (
    <div className="mobile-submenu">
      <button type="button" ref={backRef} onClick={onBack} className="mobile-submenu-back">
        <ChevronLeft size={16} aria-hidden="true" />
        Back
      </button>

      <div className="mobile-submenu-heading">{link.label}</div>

      <div className="mobile-submenu-list">
        {link.drillGroups ? (
          // e.g. IT Services — show subsection names as a second drill-in
          // level instead of dumping every group's items at once.
          link.children.map((group) => (
            <button
              key={group.heading}
              type="button"
              onClick={() => onDrillGroup(group)}
              className="mobile-nav-trigger"
            >
              {group.heading}
              <ChevronRight size={16} className="nav-chevron-right" aria-hidden="true" />
            </button>
          ))
        ) : link.megaColumns === 1 ? (
          link.children.map((item) => (
            <Link key={item.label} to={item.href} onClick={onNavigate}>
              <MegaItem item={item} />
            </Link>
          ))
        ) : (
         link.children.map((group) => (
  <div
    key={group.heading || group.items[0].label}
    className={`mobile-menu-group ${group.heading ? '' : 'mobile-menu-group--flat'}`}
  >
    {group.heading && <div className="mobile-menu-group-heading">{group.heading}</div>}
    {group.items.map((item) => (
      <Link key={item.label} to={item.href} onClick={onNavigate}>
        <MegaItem item={item} />
      </Link>
    ))}
  </div>
))
        )}
      </div>
    </div>
  )
}

// ── Mobile subsection items view (level 3 — only reachable from a
// drillGroups link like IT Services): Back button + heading + items ──
function MobileSubGroup({ group, onBack, onNavigate }) {
  const backRef = useRef(null)
  useEffect(() => {
    const t = setTimeout(() => backRef.current?.focus(), 360)
    return () => clearTimeout(t)
  }, [group])

  return (
    <div className="mobile-submenu">
      <button type="button" ref={backRef} onClick={onBack} className="mobile-submenu-back">
        <ChevronLeft size={16} aria-hidden="true" />
        Back
      </button>

      <div className="mobile-submenu-heading">{group.heading}</div>

      <div className="mobile-submenu-list">
        {group.items.map((item) => (
          <Link key={item.label} to={item.href} onClick={onNavigate}>
            <MegaItem item={item} />
          </Link>
        ))}
      </div>
    </div>
  )
}

// ── Animated sun/moon theme toggle ──
// function ThemeToggle({ isDark, toggle }) {
//   return (
//     <button onClick={toggle} aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`} className="theme-toggle-btn">
//       <span className={`theme-icon ${isDark ? 'theme-icon--visible' : 'theme-icon--hidden-up'}`}>
//         <Sun size={17} />
//       </span>
//       <span className={`theme-icon ${!isDark ? 'theme-icon--visible' : 'theme-icon--hidden-down'}`}>
//         <Moon size={17} />
//       </span>
//     </button>
//   )
// }

// ── Main Navbar ───────────────────────────────────────────────
export default function Navbar() {
  const {  isDark } = useTheme()
  const scrolled = useScrolled(24)
  const { isMobile, isTablet } = useWindowSize()
  const [menuOpen, setMenuOpen] = useState(false)
  // Two-level mobile drill-down state:
  // - mobileSubmenuLink:  which top-level NAV_LINKS entry is open (null = root)
  // - mobileSubmenuGroup: which subsection *within* that link is open — only
  //   ever set for links with `drillGroups: true` (currently just IT Services)
  const [mobileSubmenuLink, setMobileSubmenuLink] = useState(null)
  const [mobileSubmenuGroup, setMobileSubmenuGroup] = useState(null)
  const isMobileView = isMobile || isTablet
  const location = useLocation()

  
    // ── Desktop mega-menu: single shared panel, driven by which link is active ──
   const [activeLink, setActiveLink] = useState(null)
  const [isSwitching, setIsSwitching] = useState(false)
  const [switchHeight, setSwitchHeight] = useState(null)
  // True for one paint while a menu is hidden with no shrink/close motion
  // (see closeMenuInstant) — e.g. moving from an open mega menu onto the
  // logo, Home, or Talk to Sales.
  const [suppressCloseAnim, setSuppressCloseAnim] = useState(false)
  const closeTimerRef = useRef(null)
  const switchResetRef = useRef(null)
  const panelRef = useRef(null)
  const panelInnerRef = useRef(null)
  const navLinksRef = useRef(null)
  const triggerRefs = useRef({})
  const prevLinkRef = useRef(null)
// ── Capsule hover tracking (desktop, pre-scroll only) ──
const [isHoveringNavbar, setIsHoveringNavbar] = useState(false)
const navHoverTimerRef = useRef(null)

const handleNavMouseEnter = () => {
  if (isMobileView) return
  clearTimeout(navHoverTimerRef.current)
  setIsHoveringNavbar(true)
}

const handleNavMouseLeave = () => {
  if (isMobileView) return
  clearTimeout(navHoverTimerRef.current)
  navHoverTimerRef.current = setTimeout(() => setIsHoveringNavbar(false), 150)
}
   // Clears any in-flight switch-height animation/timer and its state —
  // called whenever the panel closes or is reset some other way, so a
  // stale height value never leaks into the next open.
  const resetSwitchState = () => {
    clearTimeout(switchResetRef.current)
    setIsSwitching(false)
    setSwitchHeight(null)
  }

  const openMenu = (link) => {
    clearTimeout(closeTimerRef.current)
    setSuppressCloseAnim(false)

    if (prevLinkRef.current && prevLinkRef.current.label !== link.label) {
      // Switching directly from one open mega menu to another — grow or
      // shrink the panel's height smoothly between the two menus' natural
      // heights (top stays pinned under the navbar; only the bottom edge
      // moves), instead of replaying the open/close reveal.
      const fromHeight = panelInnerRef.current?.getBoundingClientRect().height
      clearTimeout(switchResetRef.current)
      setSwitchHeight(fromHeight || null)
      setIsSwitching(true)
    } else {
      resetSwitchState()
    }

    setActiveLink(link)
    prevLinkRef.current = link
  }

  // Small delay on close so moving the mouse from the trigger down into
  // the panel doesn't momentarily close it before the pointer arrives.
  const closeMenu = (immediate = false) => {
    clearTimeout(closeTimerRef.current)
    if (immediate) {
      resetSwitchState()
      setActiveLink(null)
      prevLinkRef.current = null
      return
    }
    closeTimerRef.current = setTimeout(() => {
      resetSwitchState()
      setActiveLink(null)
      prevLinkRef.current = null
    }, 150)
  }

  // Home / Talk to Sales / Logo — moving from an open mega menu onto any
  // of these should never read as the panel "closing"; hide it instantly,
  // with no shrink/collapse motion, then restore normal transitions.
  const closeMenuInstant = () => {
    if (!activeLink) return
    clearTimeout(closeTimerRef.current)
    resetSwitchState()
    setSuppressCloseAnim(true)
    setActiveLink(null)
    prevLinkRef.current = null
    // Let the instant-hide paint first, then re-enable transitions so
    // the next open/close animates normally again.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setSuppressCloseAnim(false))
    })
  }

  useEffect(() => () => {
    clearTimeout(closeTimerRef.current)
    clearTimeout(navHoverTimerRef.current)
    clearTimeout(switchResetRef.current)
  }, [])

  // While switching between two open menus: let the new content render
  // at its natural height first, then animate the panel to that height
  // on the next frame — gives the browser a start value (fromHeight, set
  // in openMenu) to transition from, so the resize is smooth rather than
  // an instant jump. Settles back to auto height once the transition
  // (0.4s, matches .mega-menu below) has finished.
  useEffect(() => {
    if (!isSwitching) return
    const raf = requestAnimationFrame(() => {
      const target = panelInnerRef.current?.scrollHeight
      if (target) setSwitchHeight(target)
    })
    switchResetRef.current = setTimeout(() => {
      setIsSwitching(false)
      setSwitchHeight(null)
    }, 420)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(switchResetRef.current)
    }
  }, [activeLink, isSwitching])

  // Escape closes the open panel and returns focus to its trigger
  useEffect(() => {
    if (!activeLink) return
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      const label = activeLink.label
      resetSwitchState()
      setActiveLink(null)
      prevLinkRef.current = null
      triggerRefs.current[label]?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [activeLink])

  // Click outside the trigger row AND the panel closes it
  useEffect(() => {
    if (!activeLink) return
    const onClick = (e) => {
      const insideTriggers = navLinksRef.current?.contains(e.target)
      const insidePanel = panelRef.current?.contains(e.target)
      if (!insideTriggers && !insidePanel) {
        resetSwitchState()
        setActiveLink(null)
        prevLinkRef.current = null
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [activeLink])

  // Tabbing past the panel's last link closes it; Shift+Tab off the first
  // link returns focus to the trigger that opened it (panel is no longer
  // DOM-nested under its trigger, so this has to be managed explicitly).
  const handlePanelKeyDown = (e) => {
    if (e.key !== 'Tab' || !panelRef.current || !activeLink) return
    const focusable = Array.from(
      panelRef.current.querySelectorAll('a[href], button:not([disabled])')
    )
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
       if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      const label = activeLink.label
      resetSwitchState()
      setActiveLink(null)
      prevLinkRef.current = null
      triggerRefs.current[label]?.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      resetSwitchState()
      setActiveLink(null)
      prevLinkRef.current = null
    }
  }

  useEffect(() => {
    document.body.style.overflow = menuOpen && isMobileView ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen, isMobileView])

   useEffect(() => {
    setMenuOpen(false)
    setMobileSubmenuLink(null)
    setMobileSubmenuGroup(null)
    resetSwitchState()
    setActiveLink(null)
    prevLinkRef.current = null
  }, [location.pathname])

  useEffect(() => {
    if (!isMobileView) {
      setMenuOpen(false)
      setMobileSubmenuLink(null)
      setMobileSubmenuGroup(null)
     } else {
      resetSwitchState()
      setActiveLink(null)
      prevLinkRef.current = null
      clearTimeout(navHoverTimerRef.current)
      setIsHoveringNavbar(false)
    }
  }, [isMobileView])

  // Wait for the drawer's own close transition (0.35s, see .mobile-menu-panel
  // in navbar.css) before resetting back to the root list — otherwise the
  // submenu would visibly snap back to the root view while still sliding away.
  useEffect(() => {
    if (menuOpen) return
    const t = setTimeout(() => {
      setMobileSubmenuLink(null)
      setMobileSubmenuGroup(null)
    }, 350)
    return () => clearTimeout(t)
  }, [menuOpen])

  // Esc: go back one level at a time (items → subsections → root → closed).
  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      if (mobileSubmenuGroup) setMobileSubmenuGroup(null)
      else if (mobileSubmenuLink) setMobileSubmenuLink(null)
      else setMenuOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [menuOpen, mobileSubmenuLink, mobileSubmenuGroup])

  const isActiveLink = (href) =>
    location.pathname === href || (href !== '/' && location.pathname.startsWith(href))

  const activeCols = activeLink?.megaColumns || 1
   const mainGroups = activeCols === 1 ? [] : activeLink?.children.filter((g) => !g.sidebar) || []
  const sidebarGroup = activeCols === 1 ? null : activeLink?.children.find((g) => g.sidebar) || null
  const bodyGridColumns =
    activeLink?.sidebarColumns ||
    `repeat(${mainGroups.length + (sidebarGroup ? 1 : 0)}, minmax(220px, 1fr))`
 //const isPillMode = scrolled || !!activeLink
 const isPillMode = scrolled || isMobileView || isHoveringNavbar || !!activeLink
  return (
        <div
  className={`navbar-wrap ${isPillMode ? 'navbar-wrap--scrolled' : ''}`}
  onMouseEnter={handleNavMouseEnter}
  onMouseLeave={handleNavMouseLeave}
>
      {/* Dim the rest of the page while a mega-menu panel is open (desktop only) */}
      {!isMobileView && (
        <div
          className="nav-page-overlay "
          aria-hidden="true"
          onClick={() => closeMenu(true)}
        />
      )}

           <header className={`container navbar-header ${isPillMode ? 'navbar-header--scrolled' : ''}`}>
        <nav className={`navbar-inner ${isPillMode ? 'navbar-inner--scrolled' : ''}`} aria-label="Main navigation">
          {/* ── Logo ── */}
                    <Link to={ROUTES.HOME} className="navbar-logo" aria-label={`${SITE.fullName} — home`} onMouseEnter={closeMenuInstant}>
            <img
              src={isDark ? (SITE.logoDark || SITE.logo) : SITE.logo}
              alt={SITE.fullName}
                           className={`navbar-logo-img ${isPillMode ? 'navbar-logo-img--scrolled' : ''}`}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling.style.display = 'flex'
              }}
            />
            <span className="navbar-logo-fallback" aria-hidden="true">{SITE.name}</span>
          </Link>

          {/* ── Desktop Links ── */}
          {!isMobileView && (
            <div className="navbar-links" ref={navLinksRef}>
                          {NAV_LINKS.map((link) => (
                <NavItem
                  key={link.label}
                  link={link}
                  isOpen={activeLink?.label === link.label}
                  onOpen={openMenu}
                  onClose={closeMenu}
                  setTriggerRef={(el) => { triggerRefs.current[link.label] = el }}
                  onInstantClose={closeMenuInstant}
                />
              ))}
            </div>
          )}

          {/* ── Controls ── */}
          <div className="navbar-controls">
            {!isMobileView && (
                           <Button size="sm" className="btn-call-action" href={ROUTES.SALES_ENQUIRY} onMouseEnter={closeMenuInstant}>
                <i className="fa-solid fa-phone" style={{ paddingRight: '0.5rem' }}></i>Talk to Sales
              </Button>
            )}

            {isMobileView && (
              <button
                onClick={() => setMenuOpen((o) => !o)}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                className={`navbar-mobile-toggle ${menuOpen ? 'navbar-mobile-toggle--open' : ''}`}
              >
                <span className="hamburger-icon" aria-hidden="true">
                  <span className="hamburger-line hamburger-line--top" />
                  <span className="hamburger-line hamburger-line--mid" />
                  <span className="hamburger-line hamburger-line--bottom" />
                </span>
              </button>
            )}
          </div>
        </nav>

        {/* ── Shared full-width mega-menu panel (desktop only) ──
            One instance for every nav item — content switches with
            `activeLink`, so the panel always renders as a single wide
            surface anchored under the navbar bar, not a per-item dropdown. */}
               {!isMobileView && (
                    <div
            ref={panelRef}
            className={`mega-menu ${activeLink ? 'mega-menu--open' : ''} ${isSwitching ? 'mega-menu--switching' : ''} ${suppressCloseAnim ? 'mega-menu--no-transition' : ''}`}
            role="menu"
            aria-hidden={!activeLink}
            aria-label={activeLink ? `${activeLink.label} submenu` : undefined}
            style={isSwitching && switchHeight ? { height: switchHeight } : undefined}
                       onMouseEnter={() => { if (activeLink) openMenu(activeLink) }}
            onMouseLeave={() => closeMenu()}
            onKeyDown={handlePanelKeyDown}
          >
          {/* Inner wrapper — natural height is measured to drive the switch-height animation */}
          <div ref={panelInnerRef} className="mega-menu__inner">
                       {activeLink && (
              <>
                {/* key forces a full remount when switching menus, so the
                    previous menu's items are torn down before the new
                    menu's items mount — prevents old/new items visually
                    mixing when hovering quickly from one menu to another */}
                                         {activeCols === 1 ? (
                  <div key={activeLink.label} className="mega-menu__grid mega-menu__grid--cols-1">
                    {activeLink.children.map((item) => (
                      <MegaItem key={item.label} item={item} onClose={() => closeMenu(true)} />
                    ))}
                  </div>
                ) : (
                  <div
                    key={activeLink.label}
                    className="mega-menu__body"
                    style={{ gridTemplateColumns: bodyGridColumns }}
                  >
                    {mainGroups.map((group) => (
                      <div className="mega-menu__column" key={group.heading}>
                        {group.heading && <div className="mega-menu__heading">{group.heading}</div>}
                        {group.items.map((item) => (
                          <MegaItem key={item.label} item={item} onClose={() => closeMenu(true)} />
                        ))}
                      </div>
                    ))}

                    {sidebarGroup && (
                      <div className="mega-menu__sidebar">
                        {sidebarGroup.heading && (
                          <div className="mega-menu__sidebar-heading">{sidebarGroup.heading}</div>
                        )}
                        {sidebarGroup.items.map((item) => (
                          <MegaItem key={item.label} item={item} onClose={() => closeMenu(true)} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                                {activeLink.pbExtra > 0 && (
                  <div
                    key={`extra-${activeLink.label}`}
                    className="mega-menu-panel-extra"
                    style={{ '--panel-extra-height': `${activeLink.pbExtra}px` }}
                    aria-hidden="true"
                  />
                )}

                {activeCols !== 1 && (
                  <div key={`footer-${activeLink.label}`} className="mega-menu-footer">
                    <Link to={ROUTES.SALES_ENQUIRY} className="mega-menu-footer-link" onClick={() => closeMenu(true)}>
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                      <span>Talk to our experts</span>
                    </Link>
                
                      <Link to={ROUTES.OUR_WORK} className="mega-menu-footer-link" onClick={() => closeMenu(true)}>
                       <i className="fa-solid fa-briefcase" aria-hidden="true"></i><span>Check out our portfolio</span></Link>
                    
                  </div>
                )}
                          </>
            )}
          </div>
          </div>
        )}

        {/* ── Mobile Menu ── */}
        {isMobileView && ReactDOM.createPortal(
          <>
            {/* Backdrop — click to close */}
            <div
              className={`mobile-menu-backdrop ${menuOpen ? 'mobile-menu-backdrop--open' : ''}`}
              aria-hidden="true"
              onClick={() => setMenuOpen(false)}
            />

            {/* Drawer panel */}
            <div
  id="mobile-menu"
  className={`mobile-menu-panel ${menuOpen ? 'mobile-menu-panel--open' : ''}`}
  role="dialog"
  aria-modal="true"
  aria-label="Navigation menu"
>
  {/* Fixed header — always visible, never scrolls */}
  <div className="mobile-menu-header">
    <img
      src={isDark ? (SITE.logoDark || SITE.logo) : SITE.logo}
      alt={SITE.fullName}
      className="mobile-drawer-logo"
      onError={(e) => {
        e.currentTarget.style.display = 'none'
      }}
    />
    <button
      type="button"
      className="mobile-drawer-close"
      onClick={() => setMenuOpen(false)}
      aria-label="Close menu"
    >
      <X size={18} />
    </button>
  </div>

  {/* Scrollable content below the fixed header */}
  <div className="mobile-menu-panel__inner">
                <div
                  className="mobile-menu-track"
                  style={{
                    transform: mobileSubmenuGroup
                      ? 'translateX(-66.6667%)'
                      : mobileSubmenuLink
                      ? 'translateX(-33.3333%)'
                      : 'translateX(0%)',
                  }}
                >
                  {/* Root view */}
                  <div className="mobile-menu-view mobile-menu-view--root" aria-hidden={!!mobileSubmenuLink}>
                    <div key={menuOpen ? 'open' : 'closed'} className="mobile-menu-list">
                      {NAV_LINKS.map((link, i) => (
                        <div
                          key={link.label}
                          className={menuOpen ? 'nav-stagger-item' : ''}
                          style={{ '--stagger-delay': `${i * 50}ms` }}
                        >
                          <MobileNavItem
                            link={link}
                            isActive={isActiveLink(link.href)}
                            onNavigate={() => setMenuOpen(false)}
                            onDrillIn={() => setMobileSubmenuLink(link)}
                          />
                        </div>
                      ))}
                      <div className="mobile-menu-cta">
                         <Button
                          href={ROUTES.SALES_ENQUIRY}
                          className="mobile-menu-cta-btn btn-call-action"
                          onClick={() => setMenuOpen(false)}
                        ><i className="fa-solid fa-phone" style={{ paddingRight: '0.5rem' }}></i>
                          Talk to Sales
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Submenu view (level 2) */}
                  <div
                    className="mobile-menu-view mobile-menu-view--submenu"
                    aria-hidden={!mobileSubmenuLink || !!mobileSubmenuGroup}
                  >
                    {mobileSubmenuLink && (
                      <MobileSubmenu
                        link={mobileSubmenuLink}
                        onBack={() => setMobileSubmenuLink(null)}
                        onNavigate={() => setMenuOpen(false)}
                        onDrillGroup={(group) => setMobileSubmenuGroup(group)}
                      />
                    )}
                  </div>

                  {/* Subsection items view (level 3 — only ever populated for
                      links with drillGroups: true, e.g. IT Services) */}
                  <div className="mobile-menu-view mobile-menu-view--subgroup" aria-hidden={!mobileSubmenuGroup}>
                    {mobileSubmenuGroup && (
                      <MobileSubGroup
                        group={mobileSubmenuGroup}
                        onBack={() => setMobileSubmenuGroup(null)}
                        onNavigate={() => setMenuOpen(false)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
      </header>
    </div>
  )
}